import { useEffect, useState } from 'react';
import type { INews, INewsCreate, NewsKind } from '@skystone/shared';
import { Button, Drawer, DrawerFooter, ErrorBanner } from '@/components/ui';
import { NEWS_KIND_ICON } from '@/lib/news-kind-icons';
import { getErrorMessage } from '@/lib/utils';

const KIND_OPTIONS: Array<{ value: NewsKind; label: string }> = [
  { value: 'RELEASED', label: 'Released' },
  { value: 'PATCHED', label: 'Patched' },
  { value: 'UPDATED', label: 'Updated' },
  { value: 'RENAMED', label: 'Renamed' },
  { value: 'ANNOUNCEMENT', label: 'Announcement' },
];

const TITLE_MAX = 80;
const DESCRIPTION_MAX = 300;
const CATEGORY_TAG_MAX = 100;

interface NewsEditorProps {
  open: boolean;
  /** When set, the form opens in edit mode pre-filled from this row. Null = create mode. */
  initialItem: INews | null;
  onClose: () => void;
  onSave: (data: INewsCreate) => Promise<void>;
}

function todayISO(): string {
  return new Date().toISOString().slice(0, 10);
}

function emptyForm(): INewsCreate {
  return {
    title: '',
    description: '',
    kind: 'RELEASED',
    categoryTag: null,
    eventDate: todayISO(),
  };
}

function fromItem(item: INews): INewsCreate {
  return {
    title: item.title,
    description: item.description,
    kind: item.kind,
    categoryTag: item.categoryTag,
    eventDate: item.eventDate,
  };
}

export function NewsEditor({ open, initialItem, onClose, onSave }: Readonly<NewsEditorProps>) {
  const [form, setForm] = useState<INewsCreate>(() =>
    initialItem ? fromItem(initialItem) : emptyForm(),
  );
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setForm(initialItem ? fromItem(initialItem) : emptyForm());
    setError(null);
    setSaving(false);
  }, [open, initialItem]);

  const KindIcon = NEWS_KIND_ICON[form.kind];

  const handleSubmit = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    setError(null);
    setSaving(true);
    try {
      await onSave({
        title: form.title.trim(),
        description: form.description.trim(),
        kind: form.kind,
        categoryTag: form.categoryTag?.trim() ? form.categoryTag.trim() : null,
        eventDate: form.eventDate,
      });
    } catch (err) {
      setError(getErrorMessage(err));
      setSaving(false);
    }
  };

  return (
    <Drawer
      open={open}
      onClose={onClose}
      title={initialItem ? 'Edit news item' : 'Create news item'}
    >
      <form onSubmit={handleSubmit} className="flex h-full flex-col gap-4">
        {error && <ErrorBanner message={error} />}

        <div className="flex flex-col gap-1">
          <label htmlFor="news-title" className="text-sm font-medium">
            Title <span className="text-error">*</span>
          </label>
          <input
            id="news-title"
            type="text"
            required
            maxLength={TITLE_MAX}
            value={form.title}
            onChange={(e) => setForm({ ...form, title: e.target.value })}
            className="rounded-md border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
          />
          <p className="text-xs text-muted-foreground">
            {form.title.length}/{TITLE_MAX}
          </p>
        </div>

        <div className="flex flex-col gap-1">
          <label htmlFor="news-description" className="text-sm font-medium">
            Description <span className="text-error">*</span>
          </label>
          <textarea
            id="news-description"
            required
            maxLength={DESCRIPTION_MAX}
            rows={4}
            value={form.description}
            onChange={(e) => setForm({ ...form, description: e.target.value })}
            className="rounded-md border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
          />
          <p className="text-xs text-muted-foreground">
            {form.description.length}/{DESCRIPTION_MAX}
          </p>
        </div>

        <div className="flex flex-col gap-1">
          <label htmlFor="news-kind" className="text-sm font-medium">
            Kind <span className="text-error">*</span>
          </label>
          <div className="flex items-center gap-2">
            <KindIcon className="h-5 w-5 text-muted-foreground" />
            <select
              id="news-kind"
              value={form.kind}
              onChange={(e) => setForm({ ...form, kind: e.target.value as NewsKind })}
              className="flex-1 rounded-md border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
            >
              {KIND_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className="flex flex-col gap-1">
          <label htmlFor="news-category" className="text-sm font-medium">
            Category tag <span className="text-muted-foreground">(optional)</span>
          </label>
          <input
            id="news-category"
            type="text"
            maxLength={CATEGORY_TAG_MAX}
            value={form.categoryTag ?? ''}
            onChange={(e) =>
              setForm({ ...form, categoryTag: e.target.value === '' ? null : e.target.value })
            }
            className="rounded-md border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
            placeholder="e.g. PHASE 1, DATA QUALITY"
          />
        </div>

        <div className="flex flex-col gap-1">
          <label htmlFor="news-date" className="text-sm font-medium">
            Event date <span className="text-error">*</span>
          </label>
          <input
            id="news-date"
            type="date"
            required
            value={form.eventDate}
            onChange={(e) => setForm({ ...form, eventDate: e.target.value })}
            className="rounded-md border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
          />
        </div>

        <DrawerFooter className="mt-auto">
          <Button type="button" variant="ghost" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button type="submit" isLoading={saving}>
            {initialItem ? 'Save changes' : 'Create'}
          </Button>
        </DrawerFooter>
      </form>
    </Drawer>
  );
}
